import React, { useState, useEffect, useCallback, useRef } from 'react';
import { vendorsApi } from '../../utils/vendorsApi';
import { api } from '../../utils/api';
import LoadingSpinner from '../../components/LoadingSpinner/LoadingSpinner';
import { useAuth } from '../../contexts/AuthContext';
import './Vendors.css';

const fmtBalance = (val) =>
  val != null
    ? Number(val).toLocaleString('en-EG', { minimumFractionDigits: 2, maximumFractionDigits: 2 })
    : '—';

// ── Vendor Form Modal ─────────────────────────────────────

function VendorModal({ vendor, onClose, onSave }) {
  const isEdit = !!vendor?.id;
  const [form, setForm] = useState({
    code:             vendor?.code             ?? '',
    name:             vendor?.name             ?? '',
    arabicName:       vendor?.arabicName        ?? '',
    accountId:        vendor?.accountId         ?? '',
    contactPerson:    vendor?.contactPerson     ?? '',
    phone:            vendor?.phone             ?? '',
    email:            vendor?.email             ?? '',
    address:          vendor?.address           ?? '',
    taxId:            vendor?.taxId             ?? '',
    paymentTermsDays: vendor?.paymentTermsDays  ?? 30,
    currencyId:       vendor?.currency?.id      ?? '',
    notes:            vendor?.notes             ?? '',
    sortOrder:        vendor?.sortOrder         ?? 0,
  });
  const [creditorAccounts, setCreditorAccounts] = useState([]);
  const [currencies, setCurrencies] = useState([]);
  const [saving, setSaving] = useState(false);
  const [errors, setErrors] = useState({});

  useEffect(() => {
    // Load creditor accounts (220.*) and currencies in parallel
    Promise.all([
      api.get('/accounts?type=POSTING&parentCode=220&limit=300'),
      api.get('/currencies?isActive=true'),
    ]).then(([accRes, curRes]) => {
      // Filter accounts whose code starts with "220-"
      const accs = (accRes?.items ?? accRes ?? []).filter((a) => a.code?.startsWith('220'));
      setCreditorAccounts(accs);
      setCurrencies(curRes?.items ?? curRes ?? []);
      if (!isEdit && (curRes?.items ?? curRes ?? []).length) {
        const base = (curRes?.items ?? curRes).find((c) => c.isBase) || (curRes?.items ?? curRes)[0];
        setForm((f) => ({ ...f, currencyId: base.id }));
      }
    }).catch(() => {});
  }, [isEdit]);

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const validate = () => {
    const e = {};
    if (!form.code.trim())  e.code  = 'Required';
    if (!form.name.trim())  e.name  = 'Required';
    if (!form.accountId && !isEdit) e.accountId = 'Required';
    if (!form.currencyId)   e.currencyId = 'Required';
    if (form.email && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(form.email)) e.email = 'Invalid email';
    if (form.paymentTermsDays < 0 || form.paymentTermsDays > 365) e.paymentTermsDays = 'Must be 0–365';
    return e;
  };

  const handleSave = async () => {
    const e = validate();
    if (Object.keys(e).length) { setErrors(e); return; }
    setSaving(true);
    try {
      const payload = {
        ...form,
        paymentTermsDays: Number(form.paymentTermsDays),
        sortOrder:        Number(form.sortOrder),
        arabicName:       form.arabicName   || undefined,
        contactPerson:    form.contactPerson || undefined,
        phone:            form.phone         || undefined,
        email:            form.email         || undefined,
        address:          form.address       || undefined,
        taxId:            form.taxId         || undefined,
        notes:            form.notes         || undefined,
      };
      const saved = isEdit
        ? await vendorsApi.update(vendor.id, payload)
        : await vendorsApi.create(payload);
      onSave(saved);
    } catch (err) {
      setErrors({ api: err.message });
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="v-modal-overlay">
      <div className="v-modal">
        <div className="v-modal-header">
          <h2 className="v-modal-title">{isEdit ? 'Edit Vendor' : 'New Vendor'}</h2>
          <button className="v-btn v-btn--icon" onClick={onClose}><i className="fas fa-times" /></button>
        </div>
        <div className="v-modal-body">
          {errors.api && (
            <div className="v-error-banner">
              <i className="fas fa-exclamation-circle" /> {errors.api}
            </div>
          )}

          <div className="v-form-grid">
            {/* Code */}
            <div className="v-field">
              <label className="v-label">Code *</label>
              <input className={`v-input${errors.code ? ' v-input--err' : ''}`}
                value={form.code} onChange={(e) => set('code', e.target.value)} placeholder="e.g. V-001" />
              {errors.code && <span className="v-field-err">{errors.code}</span>}
            </div>

            {/* Name */}
            <div className="v-field">
              <label className="v-label">Name (English) *</label>
              <input className={`v-input${errors.name ? ' v-input--err' : ''}`}
                value={form.name} onChange={(e) => set('name', e.target.value)} />
              {errors.name && <span className="v-field-err">{errors.name}</span>}
            </div>

            {/* Arabic name */}
            <div className="v-field">
              <label className="v-label">Name (Arabic)</label>
              <input className="v-input" dir="rtl"
                value={form.arabicName} onChange={(e) => set('arabicName', e.target.value)} />
            </div>

            {/* AP Account (only on create) */}
            {!isEdit && (
              <div className="v-field">
                <label className="v-label">AP Account (220.*) *</label>
                <select className={`v-select${errors.accountId ? ' v-input--err' : ''}`}
                  value={form.accountId} onChange={(e) => set('accountId', e.target.value)}>
                  <option value="">— select account —</option>
                  {creditorAccounts.map((a) => (
                    <option key={a.id} value={a.id}>{a.code} — {a.name}</option>
                  ))}
                </select>
                {errors.accountId && <span className="v-field-err">{errors.accountId}</span>}
              </div>
            )}
            {isEdit && (
              <div className="v-field">
                <label className="v-label">AP Account</label>
                <input className="v-input" readOnly
                  value={`${vendor.account?.code} — ${vendor.account?.name}`} />
              </div>
            )}

            {/* Currency */}
            <div className="v-field">
              <label className="v-label">Currency *</label>
              <select className={`v-select${errors.currencyId ? ' v-input--err' : ''}`}
                value={form.currencyId} onChange={(e) => set('currencyId', e.target.value)}>
                <option value="">— select —</option>
                {currencies.map((c) => (
                  <option key={c.id} value={c.id}>{c.code} {c.symbol ? `(${c.symbol})` : ''}</option>
                ))}
              </select>
              {errors.currencyId && <span className="v-field-err">{errors.currencyId}</span>}
            </div>

            {/* Payment terms */}
            <div className="v-field">
              <label className="v-label">Payment Terms (days)</label>
              <input className={`v-input${errors.paymentTermsDays ? ' v-input--err' : ''}`}
                type="number" min="0" max="365"
                value={form.paymentTermsDays} onChange={(e) => set('paymentTermsDays', e.target.value)} />
              {errors.paymentTermsDays && <span className="v-field-err">{errors.paymentTermsDays}</span>}
            </div>

            {/* Tax ID */}
            <div className="v-field">
              <label className="v-label">Tax Registration No.</label>
              <input className="v-input"
                value={form.taxId} onChange={(e) => set('taxId', e.target.value)} />
            </div>

            {/* Contact person */}
            <div className="v-field">
              <label className="v-label">Contact Person</label>
              <input className="v-input"
                value={form.contactPerson} onChange={(e) => set('contactPerson', e.target.value)} />
            </div>

            {/* Phone */}
            <div className="v-field">
              <label className="v-label">Phone</label>
              <input className="v-input" type="tel"
                value={form.phone} onChange={(e) => set('phone', e.target.value)} />
            </div>

            {/* Email */}
            <div className="v-field">
              <label className="v-label">Email</label>
              <input className={`v-input${errors.email ? ' v-input--err' : ''}`} type="email"
                value={form.email} onChange={(e) => set('email', e.target.value)} />
              {errors.email && <span className="v-field-err">{errors.email}</span>}
            </div>

            {/* Address — full width */}
            <div className="v-field v-field--full">
              <label className="v-label">Address</label>
              <textarea className="v-input v-textarea" rows={2}
                value={form.address} onChange={(e) => set('address', e.target.value)} />
            </div>

            {/* Notes */}
            <div className="v-field v-field--full">
              <label className="v-label">Notes</label>
              <textarea className="v-input v-textarea" rows={2}
                value={form.notes} onChange={(e) => set('notes', e.target.value)} />
            </div>
          </div>
        </div>
        <div className="v-modal-footer">
          <button className="v-btn v-btn--ghost" onClick={onClose} disabled={saving}>Cancel</button>
          <button className="v-btn v-btn--primary" onClick={handleSave} disabled={saving}>
            {saving ? <LoadingSpinner variant="button" /> : isEdit ? 'Save Changes' : 'Create Vendor'}
          </button>
        </div>
      </div>
    </div>
  );
}

// ── Vendor Detail / Balance Modal ──────────────────────────

function VendorDetailModal({ vendor: initial, onClose, onEdit }) {
  const [vendor, setVendor] = useState(initial);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    vendorsApi.getById(initial.id)
      .then(setVendor)
      .catch(() => {})
      .finally(() => setLoading(false));
  }, [initial.id]);

  return (
    <div className="v-modal-overlay">
      <div className="v-modal v-modal--detail">
        <div className="v-modal-header">
          <h2 className="v-modal-title">{vendor.name}</h2>
          <button className="v-btn v-btn--icon" onClick={onClose}><i className="fas fa-times" /></button>
        </div>
        <div className="v-modal-body">
          {loading ? (
            <LoadingSpinner variant="default" />
          ) : (
            <>
              <div className="v-balance-card">
                <span className="v-balance-label">AP Balance (EGP)</span>
                <span className={`v-balance-amount ${Number(vendor.balance) >= 0 ? 'positive' : 'negative'}`}>
                  {fmtBalance(vendor.balance)}
                </span>
                <span className="v-balance-note">
                  {Number(vendor.balance) >= 0 ? 'We owe vendor' : 'Vendor owes us'}
                </span>
              </div>

              <div className="v-detail-grid">
                <div className="v-detail-row"><span className="v-detail-key">Code</span><span className="v-detail-val">{vendor.code}</span></div>
                <div className="v-detail-row"><span className="v-detail-key">Arabic Name</span><span className="v-detail-val" dir="rtl">{vendor.arabicName || '—'}</span></div>
                <div className="v-detail-row"><span className="v-detail-key">AP Account</span><span className="v-detail-val">{vendor.account?.code} — {vendor.account?.name}</span></div>
                <div className="v-detail-row"><span className="v-detail-key">Currency</span><span className="v-detail-val">{vendor.currency?.code}</span></div>
                <div className="v-detail-row"><span className="v-detail-key">Payment Terms</span><span className="v-detail-val">{vendor.paymentTermsDays} days</span></div>
                <div className="v-detail-row"><span className="v-detail-key">Tax Reg. No.</span><span className="v-detail-val">{vendor.taxId || '—'}</span></div>
                <div className="v-detail-row"><span className="v-detail-key">Contact Person</span><span className="v-detail-val">{vendor.contactPerson || '—'}</span></div>
                <div className="v-detail-row"><span className="v-detail-key">Phone</span><span className="v-detail-val">{vendor.phone || '—'}</span></div>
                <div className="v-detail-row"><span className="v-detail-key">Email</span><span className="v-detail-val">{vendor.email || '—'}</span></div>
                <div className="v-detail-row v-detail-row--full"><span className="v-detail-key">Address</span><span className="v-detail-val">{vendor.address || '—'}</span></div>
                <div className="v-detail-row v-detail-row--full"><span className="v-detail-key">Notes</span><span className="v-detail-val">{vendor.notes || '—'}</span></div>
              </div>
            </>
          )}
        </div>
        <div className="v-modal-footer">
          <button className="v-btn v-btn--ghost" onClick={onClose}>Close</button>
          <button className="v-btn v-btn--primary" onClick={() => { onClose(); onEdit(vendor); }}>
            <i className="fas fa-edit" style={{ marginRight: 6 }} /> Edit
          </button>
        </div>
      </div>
    </div>
  );
}

// ── Migrate Accounts Modal ────────────────────────────────

function MigrateModal({ onClose, onDone }) {
  const [preview, setPreview] = useState(null);
  const [loading, setLoading] = useState(false);
  const [migrating, setMigrating] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    setLoading(true);
    vendorsApi.migrateAccounts(true)
      .then(setPreview)
      .catch((e) => setError(e.message))
      .finally(() => setLoading(false));
  }, []);

  const handleMigrate = async () => {
    setMigrating(true);
    setError(null);
    try {
      const result = await vendorsApi.migrateAccounts(false);
      onDone(result);
    } catch (e) {
      setError(e.message);
      setMigrating(false);
    }
  };

  return (
    <div className="v-modal-overlay">
      <div className="v-modal">
        <div className="v-modal-header">
          <h2 className="v-modal-title">Migrate 220.* Accounts to Vendors</h2>
          <button className="v-btn v-btn--icon" onClick={onClose}><i className="fas fa-times" /></button>
        </div>
        <div className="v-modal-body">
          {error && <div className="v-error-banner"><i className="fas fa-exclamation-circle" /> {error}</div>}
          {loading && <LoadingSpinner variant="default" />}
          {preview && !loading && (
            <>
              <div className="v-migrate-summary">
                <span><strong>{preview.total}</strong> sub-accounts found under 220 CREDITORS</span>
                <span><strong>{preview.alreadyMigrated}</strong> already have a vendor record</span>
                <span><strong>{preview.wouldCreate?.length ?? 0}</strong> will be created</span>
              </div>
              {preview.wouldCreate?.length > 0 ? (
                <table className="v-table v-table--sm">
                  <thead>
                    <tr><th>Account Code</th><th>Account Name</th><th>Vendor Code</th></tr>
                  </thead>
                  <tbody>
                    {preview.wouldCreate.map((r) => (
                      <tr key={r.accountCode}>
                        <td>{r.accountCode}</td>
                        <td>{r.accountName}</td>
                        <td>{r.vendorCode}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              ) : (
                <p style={{ color: 'var(--color-success)', marginTop: 12 }}>
                  <i className="fas fa-check-circle" style={{ marginRight: 6 }} />
                  All 220.* accounts already have vendor records.
                </p>
              )}
            </>
          )}
        </div>
        <div className="v-modal-footer">
          <button className="v-btn v-btn--ghost" onClick={onClose}>Cancel</button>
          {preview?.wouldCreate?.length > 0 && (
            <button className="v-btn v-btn--primary" onClick={handleMigrate} disabled={migrating}>
              {migrating ? <LoadingSpinner variant="button" /> : `Create ${preview.wouldCreate.length} Vendors`}
            </button>
          )}
        </div>
      </div>
    </div>
  );
}

// ── Main Page ─────────────────────────────────────────────

export default function VendorsPage() {
  const { hasPermission } = useAuth();
  const canManage = hasPermission('vendors.manage');

  const [data, setData] = useState({ total: 0, items: [] });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const [search, setSearch] = useState('');
  const [showInactive, setShowInactive] = useState(false);
  const [page, setPage] = useState(1);
  const LIMIT = 50;

  const [modal, setModal] = useState(null); // null | 'create' | 'edit' | 'detail' | 'migrate'
  const [selected, setSelected] = useState(null);
  const [toast, setToast] = useState(null);

  const searchRef = useRef(null);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await vendorsApi.list({
        search:   search || undefined,
        isActive: showInactive ? undefined : true,
        page,
        limit: LIMIT,
      });
      setData(res);
    } catch (e) {
      setError(e.message);
    } finally {
      setLoading(false);
    }
  }, [search, showInactive, page]);

  useEffect(() => { load(); }, [load]);

  const showToast = (msg, type = 'success') => {
    setToast({ msg, type });
    setTimeout(() => setToast(null), 3500);
  };

  const handleSaved = (vendor) => {
    setModal(null);
    setSelected(null);
    showToast(`Vendor "${vendor.name}" saved.`);
    load();
  };

  const handleMigrateDone = (result) => {
    setModal(null);
    showToast(`Migration complete — ${result.created?.length ?? 0} vendor(s) created.`);
    load();
  };

  const handleDeactivate = async (vendor) => {
    if (!window.confirm(`Deactivate vendor "${vendor.name}"?`)) return;
    try {
      await vendorsApi.deactivate(vendor.id);
      showToast(`Vendor "${vendor.name}" deactivated.`);
      load();
    } catch (e) {
      showToast(e.message, 'error');
    }
  };

  const totalPages = Math.ceil(data.total / LIMIT);

  return (
    <div className="v-page">
      {/* Toast */}
      {toast && (
        <div className={`v-toast v-toast--${toast.type}`}>
          <i className={`fas ${toast.type === 'error' ? 'fa-exclamation-circle' : 'fa-check-circle'}`} />
          {toast.msg}
        </div>
      )}

      {/* Header */}
      <div className="v-page-header">
        <div className="v-page-title-group">
          <h1 className="v-page-title">
            <i className="fas fa-building" style={{ marginRight: 10 }} />
            Vendors
          </h1>
          <span className="v-badge">{data.total} total</span>
        </div>
        <div className="v-page-actions">
          {canManage && (
            <>
              <button className="v-btn v-btn--ghost" onClick={() => setModal('migrate')}>
                <i className="fas fa-database" style={{ marginRight: 6 }} /> Migrate Accounts
              </button>
              <button className="v-btn v-btn--primary" onClick={() => { setSelected(null); setModal('create'); }}>
                <i className="fas fa-plus" style={{ marginRight: 6 }} /> New Vendor
              </button>
            </>
          )}
        </div>
      </div>

      {/* Filters */}
      <div className="v-filters">
        <div className="v-search-wrap">
          <i className="fas fa-search v-search-icon" />
          <input
            ref={searchRef}
            className="v-search"
            placeholder="Search name, code, tax ID, phone…"
            value={search}
            onChange={(e) => { setSearch(e.target.value); setPage(1); }}
          />
        </div>
        <label className="v-toggle-label">
          <input type="checkbox" checked={showInactive}
            onChange={(e) => { setShowInactive(e.target.checked); setPage(1); }} />
          Show inactive
        </label>
      </div>

      {/* Table */}
      {loading ? (
        <LoadingSpinner variant="fullpage" />
      ) : error ? (
        <div className="v-error-banner"><i className="fas fa-exclamation-circle" /> {error}</div>
      ) : data.items.length === 0 ? (
        <div className="v-empty">
          <i className="fas fa-building" />
          <p>{search ? 'No vendors match your search.' : 'No vendors yet. Click "New Vendor" or "Migrate Accounts" to get started.'}</p>
        </div>
      ) : (
        <>
          <div className="v-table-wrap">
            <table className="v-table">
              <thead>
                <tr>
                  <th>Code</th>
                  <th>Name</th>
                  <th>AP Account</th>
                  <th>Currency</th>
                  <th>Terms</th>
                  <th>Phone</th>
                  <th>Status</th>
                  <th></th>
                </tr>
              </thead>
              <tbody>
                {data.items.map((v) => (
                  <tr key={v.id} className="v-row" onClick={() => { setSelected(v); setModal('detail'); }}>
                    <td className="v-code">{v.code}</td>
                    <td>
                      <div className="v-name">{v.name}</div>
                      {v.arabicName && <div className="v-arabic" dir="rtl">{v.arabicName}</div>}
                    </td>
                    <td className="v-account">{v.account?.code}</td>
                    <td>{v.currency?.code}</td>
                    <td>{v.paymentTermsDays}d</td>
                    <td>{v.phone || '—'}</td>
                    <td>
                      <span className={`v-status ${v.isActive ? 'active' : 'inactive'}`}>
                        {v.isActive ? 'Active' : 'Inactive'}
                      </span>
                    </td>
                    <td className="v-actions" onClick={(e) => e.stopPropagation()}>
                      {canManage && (
                        <>
                          <button className="v-btn v-btn--icon v-btn--sm"
                            title="Edit"
                            onClick={() => { setSelected(v); setModal('edit'); }}>
                            <i className="fas fa-edit" />
                          </button>
                          {v.isActive && (
                            <button className="v-btn v-btn--icon v-btn--sm v-btn--danger"
                              title="Deactivate"
                              onClick={() => handleDeactivate(v)}>
                              <i className="fas fa-ban" />
                            </button>
                          )}
                        </>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {/* Pagination */}
          {totalPages > 1 && (
            <div className="v-pagination">
              <button className="v-btn v-btn--ghost v-btn--sm"
                disabled={page <= 1} onClick={() => setPage((p) => p - 1)}>
                <i className="fas fa-chevron-left" />
              </button>
              <span className="v-page-info">Page {page} of {totalPages}</span>
              <button className="v-btn v-btn--ghost v-btn--sm"
                disabled={page >= totalPages} onClick={() => setPage((p) => p + 1)}>
                <i className="fas fa-chevron-right" />
              </button>
            </div>
          )}
        </>
      )}

      {/* Modals */}
      {(modal === 'create' || modal === 'edit') && (
        <VendorModal
          vendor={modal === 'edit' ? selected : null}
          onClose={() => { setModal(null); setSelected(null); }}
          onSave={handleSaved}
        />
      )}
      {modal === 'detail' && selected && (
        <VendorDetailModal
          vendor={selected}
          onClose={() => { setModal(null); setSelected(null); }}
          onEdit={(v) => { setSelected(v); setModal('edit'); }}
        />
      )}
      {modal === 'migrate' && (
        <MigrateModal
          onClose={() => setModal(null)}
          onDone={handleMigrateDone}
        />
      )}
    </div>
  );
}
